import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Fragment, useMemo } from 'react';
import Container from 'src/components-v2/Layout/Container/Container';
import Section from 'src/components-v2/Layout/Section/Section';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { StyledTable, TableScroll } from './Table.styled';
import type {
  CmsTableProps,
  TableCellData,
  TableData,
  TableSection,
} from './Table.types';
import { parseMarkdown, parseTableConfig, type Inline } from './Table.utils';

const SECTIONS: { section: TableSection; Tag: 'thead' | 'tbody' | 'tfoot' }[] =
  [
    { section: 'head', Tag: 'thead' },
    { section: 'body', Tag: 'tbody' },
    { section: 'foot', Tag: 'tfoot' },
  ];

const renderInline = (inline: Inline[]) => {
  return inline.map((part, index) => {
    switch (part.type) {
      case 'strong':
        return <strong key={index}>{part.text}</strong>;
      case 'em':
        return <em key={index}>{part.text}</em>;
      case 'link':
        return (
          <a key={index} href={part.href}>
            {part.text}
          </a>
        );
      default:
        return <Fragment key={index}>{part.text}</Fragment>;
    }
  });
};

const CellContent = ({ cell }: { cell: TableCellData }) => {
  if (cell.kind === 'empty') {
    return null;
  }

  if (cell.kind === 'text') {
    return <>{cell.value}</>;
  }

  return (
    <>
      {parseMarkdown(cell.value).map((block, index) => {
        return block.type === 'list' ? (
          <ul key={index} className='cms-table-list'>
            {block.items.map((item, itemIndex) => {
              return <li key={itemIndex}>{renderInline(item)}</li>;
            })}
          </ul>
        ) : (
          <p key={index} className='cms-table-paragraph'>
            {renderInline(block.inline)}
          </p>
        );
      })}
    </>
  );
};

const TableRows = ({
  table,
  section,
}: {
  table: TableData;
  section: TableSection;
}) => {
  return (
    <>
      {table.rows
        .filter((row) => {
          return row.section === section;
        })
        .map((row, rowIndex) => {
          return (
            <tr
              key={rowIndex}
              className={
                row.highlight
                  ? 'cms-table-row cms-table-row--highlight'
                  : 'cms-table-row'
              }
            >
              {row.cells.map((cell, cellIndex) => {
                const Cell = cell.header ? 'th' : 'td';

                return (
                  <Cell
                    key={cellIndex}
                    className={
                      cell.header ? 'cms-table-header' : 'cms-table-cell'
                    }
                    scope={cell.header}
                    colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                  >
                    <CellContent cell={cell} />
                  </Cell>
                );
              })}
            </tr>
          );
        })}
    </>
  );
};

/**
 * Component - Table: draws the table kept in the Table Config field. Nothing is
 * drawn when the field holds something that is not a table.
 */
const CmsTable = ({ fields, sys }: CmsTableProps) => {
  const table = useMemo(() => {
    return parseTableConfig(fields?.tableConfig);
  }, [fields?.tableConfig]);

  // The table has no mode of its own. A Background Container passes its mode
  // down in fields.mode, as it does to every component inside it, so the table
  // reads on a dark background as well as a light one. Called without those, it
  // would always answer light and override the container's theme.
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);
  const inspectorProps = useContentfulInspectorMode({ entryId: sys?.id });

  if (!table) {
    return null;
  }

  return (
    <ThemeProvider theme={componentTheme}>
      <Section>
        <Container>
          {/* A scrolling area has to be focusable, or a keyboard user cannot reach what is off screen. */}
          <TableScroll
            className='cms-table-scroll'
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role='region'
            aria-label={fields?.caption || 'Table'}
          >
            <StyledTable
              className='cms-table'
              data-entry-id={sys?.id}
              {...inspectorProps({ fieldId: 'tableConfig' })}
            >
              {fields?.caption && (
                <caption className='cms-table-caption'>
                  {fields.caption}
                </caption>
              )}
              {SECTIONS.map(({ section, Tag }) => {
                const has = table.rows.some((row) => {
                  return row.section === section;
                });

                return has ? (
                  <Tag key={section} className={`cms-table-${section}`}>
                    <TableRows table={table} section={section} />
                  </Tag>
                ) : null;
              })}
            </StyledTable>
          </TableScroll>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

export default CmsTable;
